import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { GoogleLogo, LinkedinLogo, Compass, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

const startGoogleAuth = () => {
  if (!API_BASE) return toast.error("Authentication service is not configured.");
  window.location.assign(`${API_BASE}/auth/google/start`);
};
const startLinkedInAuth = () => {
  if (!API_BASE) return toast.error("Authentication service is not configured.");
  window.location.assign(`${API_BASE}/auth/linkedin/start`);
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("email");
  const [loading, setLoading] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("oauth_error");
    if (error === "linkedin_email") toast.error("LinkedIn did not provide an email address. Please use email login.");
    else if (error === "linkedin") toast.error("LinkedIn login failed. Please try again.");
    else if (error === "google_email") toast.error("Google did not provide a verified email address. Please use email login.");
    else if (error === "google") toast.error("Google login failed. Please try again.");
    if (error) window.history.replaceState({}, "", window.location.pathname);
  }, [location]);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setInterval(() => setRetryAfter((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [retryAfter]);

  const continueEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/start", { email, purpose: "signup" });
      if (data.status === "existing") {
        setMode("password");
        toast.success("Account found. Enter your password to continue.");
      } else if (data.status === "password_setup") {
        toast.error("This account still needs its first password. Please use Google or LinkedIn, or contact support.");
      } else {
        setMode("otp");
        setRetryAfter(data.retry_after || 60);
        toast.success("Verification code sent to your email.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to continue with email.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your email.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify", { email, code, purpose: "signup" });
      sessionStorage.setItem("cgps_email_setup_token", data.setup_token);
      sessionStorage.setItem("cgps_email_setup_email", email.trim().toLowerCase());
      sessionStorage.setItem("cgps_email_setup_purpose", "signup");
      nav("/create-password", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed.");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    if (retryAfter > 0 || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/start", { email, purpose: "signup" });
      setRetryAfter(data.retry_after || 60);
      toast.success("A new verification code was sent.");
    } catch (err) { toast.error(err.response?.data?.detail || "Unable to resend the code."); }
    finally { setLoading(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      nav(user.onboarded ? (location.state?.from?.pathname || "/dashboard") : "/onboarding");
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="login-page">
      <div className="px-6 py-6"><Link to="/" className="inline-flex items-center gap-2"><Compass size={22} weight="duotone" className="text-blue-500" /><span className="font-medium tracking-tight">Career GPS <span className="text-blue-500">AI</span></span></Link></div>
      <div className="flex-1 flex items-center justify-center px-6 pb-12"><div className="w-full max-w-md">
        <h1 className="text-4xl tracking-tight font-medium">Welcome back</h1>
        <p className="text-zinc-500 mt-2">Continue your career journey.</p>
        <button type="button" onClick={startGoogleAuth} data-testid="google-login-btn" className="mt-8 w-full flex items-center justify-center gap-3 bg-white text-zinc-900 hover:bg-zinc-100 font-medium py-3 rounded-lg transition-colors"><GoogleLogo size={20} weight="bold" />Continue with Google</button>
        <button type="button" onClick={startLinkedInAuth} data-testid="linkedin-login-btn" className="mt-3 w-full flex items-center justify-center gap-3 bg-[#0A66C2] hover:bg-[#004182] text-white font-medium py-3 rounded-lg transition-colors"><LinkedinLogo size={20} weight="fill" />Continue with LinkedIn</button>
        <div className="flex items-center my-6 gap-4 text-zinc-600 text-xs"><div className="flex-1 h-px bg-white/10" />OR EMAIL<div className="flex-1 h-px bg-white/10" /></div>

        {mode === "otp" ? (
          <form onSubmit={verifyOtp} className="space-y-4">
            <button type="button" onClick={() => { setMode("email"); setCode(""); }} className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"><ArrowLeft size={14} />Change email</button>
            <div><label className="text-xs text-zinc-400 uppercase tracking-widest">Verification code</label><p className="text-sm text-zinc-500 mt-1">We sent a 6-digit code to <span className="text-white">{email}</span>.</p><input inputMode="numeric" maxLength={6} pattern="[0-9]{6}" required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="login-otp-input" className="mt-3 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 text-center tracking-[0.5em] rounded-md px-4 py-4 text-2xl text-white outline-none" /></div>
            <button type="submit" disabled={loading || code.length !== 6} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg">{loading ? "Verifying..." : "Verify email"}</button>
            <button type="button" disabled={retryAfter > 0 || loading} onClick={resendOtp} className="w-full text-sm text-blue-400 disabled:text-zinc-600">{retryAfter > 0 ? `Resend code in ${retryAfter}s` : "Resend code"}</button>
          </form>
        ) : mode === "password" ? (
          <form onSubmit={submitPassword} className="space-y-4">
            <div><label className="text-xs text-zinc-400 uppercase tracking-widest">Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="login-email-input" className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none" /></div>
            <div><div className="flex items-center justify-between"><label className="text-xs text-zinc-400 uppercase tracking-widest">Password</label><Link to="/forgot-password" className="text-xs text-blue-400 hover:text-blue-300">Forgot?</Link></div><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password-input" className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none" /></div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg">{loading ? "Signing in..." : "Sign in"}</button>
            <button type="button" onClick={() => setMode("email")} className="w-full text-sm text-zinc-400 hover:text-white">Use a different email</button>
          </form>
        ) : (
          <form onSubmit={continueEmail} className="space-y-4">
            <div><label className="text-xs text-zinc-400 uppercase tracking-widest">Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="login-email-input" placeholder="you@example.com" className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none" /></div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg">{loading ? "Checking..." : "Continue with email"}</button>
          </form>
        )}
        <p className="mt-6 text-sm text-zinc-500 text-center">No account? <Link to="/signup" className="text-blue-400 hover:text-blue-300">Sign up</Link></p>
      </div></div>
    </div>
  );
}
